import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Ban, Copy, Pause, Play, RefreshCw, RotateCcw, Search } from 'lucide-react';
import { api } from '@/lib/api';
import { PrintDocumentType, PrintJobStatus, VENUE_EVENTS } from '@/lib/shared-types';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useRealtime, useRealtimeResync } from '@/Hooks/useRealtime';
import { useConfirm } from '@/components/ui/confirm';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Select } from '@/components/ui/field';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  FLEET_PERMISSIONS,
  FLEET_ROUTES,
  PRINT_ERROR_CODES,
  errorCode,
  type FleetDestination,
  type FleetHealthRollup,
  type FleetPrinter,
  type PrintJobRow,
} from '../fleetApi';
import { JOB_VISUALS, JobStatusChip, rowStateClass } from '../fleetVisuals';
import {
  apiMessage,
  formatDateTime,
  operationKey,
  useAutoRefresh,
  useBranches,
  useQueryParams,
} from '../fleetHelpers';
import PanelResult from './PanelResult';
import PrintJobDetailDialog from './PrintJobDetailDialog';

/**
 * How often the spool re-reads itself while somebody is looking at it.
 *
 * A timer *as well as* the two socket subscriptions below, and the split is not
 * belt and braces — the two cover different halves of the screen:
 *
 *  - **Failure arrives on the socket.** `print_job.failed` and `print_job.dead`
 *    are broadcast to the branch room, so the row an operator is waiting to see
 *    turn red does so immediately.
 *  - **Success does not.** There is no `print_job.printed` event, and
 *    `print_job.queued` is deliberately never broadcast — every kitchen ticket
 *    in the venue would fire it, and a channel that never stops is a channel
 *    nobody reads. So QUEUED → CLAIMED → PRINTED is invisible to the socket and
 *    the timer is what makes it move.
 *
 * Fifteen seconds against a one-poll-interval delivery model: the bridge's own
 * latency is already the floor on how fresh this screen can be, so a tighter
 * interval buys nothing but load. The timer pauses while the tab is hidden — see
 * `useAutoRefresh`.
 */
const REFRESH_MS = 15_000;

const STATUSES = Object.values(PrintJobStatus);
const DOCUMENT_TYPES = Object.values(PrintDocumentType);

/** A job in one of these has attempts left or is out of them; both may retry. */
const RETRYABLE: PrintJobStatus[] = ['FAILED', 'DEAD'];
/** A claimed job may already be on paper, so it can no longer be stopped. */
const CANCELLABLE: PrintJobStatus[] = ['QUEUED', 'FAILED', 'DEAD'];

/**
 * ## The print job spool
 *
 * This is the screen an operator opens at 8pm on a Friday when tickets stop
 * coming out, and everything about it is arranged for that minute.
 *
 * **The rollup is the first thing on it**, because the question being asked is
 * "is anything broken", not "show me a list". `GET /devices/fleet/health`
 * answers in counts for exactly that reason, and each count here is a button
 * that filters the list beneath it — the shortest path from "three dead" to the
 * three rows.
 *
 * **Rows carry their state in form** — a coloured spine down the start edge, a
 * glyph with its own silhouette, and a tint on the two states that need
 * somebody to move. See `fleetVisuals.tsx`; the short version is that a status
 * *column* is only readable by stopping to read it, and nobody is stopping.
 *
 * **The three write actions are deliberately different shapes**, because that is
 * what makes a doubled ticket impossible rather than merely unlikely
 * (ADR-0009 §5):
 *
 *   - **Retry** moves an existing row back to `QUEUED`. It mints nothing, so it
 *     cannot double anything, and pressing it twice is a no-op. No confirmation.
 *   - **Reprint** mints a second job, and is refused by the server unless the
 *     original actually `PRINTED`. That refusal is the paper-jam rule: a jammed
 *     job is going to print on its own, and reprinting it is precisely the
 *     doubled kitchen ticket this epic exists to prevent. So the button is only
 *     offered on a printed job, it confirms first, and it carries an idempotency
 *     key so a double-tap collapses to one copy.
 *   - **Cancel** stops a job no bridge has taken yet, and is refused once one
 *     has — a status saying CANCELLED over paper that exists is a worse lie than
 *     a status saying PRINTED over paper that jammed.
 */
export default function PrintSpoolPanel() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const confirm = useConfirm();
  const branches = useBranches();

  const [branchId, setBranchId] = useState('');
  const [status, setStatus] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [destinationId, setDestinationId] = useState('');
  const [printerId, setPrinterId] = useState('');
  const [live, setLive] = useState(true);
  const [openJobId, setOpenJobId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const canRetry = can(FLEET_PERMISSIONS.jobsRetry);
  const canReprint = can(FLEET_PERMISSIONS.jobsReprint);
  const canCancel = can(FLEET_PERMISSIONS.jobsCancel);
  const canSeeRollup = can(FLEET_PERMISSIONS.devicesView);
  const canSeePrinters = can(FLEET_PERMISSIONS.printersView);

  const filtered = Boolean(branchId || status || documentType || destinationId || printerId);
  const clearFilters = () => {
    setBranchId('');
    setStatus('');
    setDocumentType('');
    setDestinationId('');
    setPrinterId('');
  };

  const params = useQueryParams({
    branchId,
    status,
    documentType,
    destinationId,
    printerId,
    limit: 100,
    sortBy: 'queuedAt',
    sortOrder: 'desc',
  });

  const jobsQuery = useApi<unknown>(FLEET_ROUTES.jobs, params);
  const rollupQuery = useApi<FleetHealthRollup>(
    canSeeRollup ? FLEET_ROUTES.fleetHealth : null,
    useQueryParams({ branchId }),
  );
  // The two pickers below are `printers.view`, which a cashier holding only
  // `print_jobs.view` does not have. They are left out rather than 403ing the
  // screen: the spool is readable without them.
  const destinationsQuery = useApi<FleetDestination[]>(
    canSeePrinters ? FLEET_ROUTES.destinations : null,
    useQueryParams({ branchId }),
  );
  const printersQuery = useApi<unknown>(
    canSeePrinters ? FLEET_ROUTES.printers : null,
    useQueryParams({ branchId, limit: 200 }),
  );

  const jobs = rowsOf<PrintJobRow>(jobsQuery.data).rows;
  const destinations = destinationsQuery.data ?? [];
  const printers = rowsOf<FleetPrinter>(printersQuery.data).rows;

  const reload = useCallback(() => {
    jobsQuery.reload();
    if (canSeeRollup) rollupQuery.reload();
    // The queries' `reload` identities change with their params, which is what
    // should re-arm the interval.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsQuery.reload, rollupQuery.reload, canSeeRollup]);

  useAutoRefresh(reload, { enabled: live, intervalMs: REFRESH_MS });

  /**
   * The two events the server actually broadcasts for this screen, both gated on
   * `print_jobs.view` — the same key `GET /printing/jobs` demands, so a socket is
   * never the cheaper way in.
   *
   * Rooms are left to `useRealtime`'s default (`branch:<the branch in the
   * header>`) when the screen is showing every branch, and pinned to the filter
   * when one is chosen — otherwise a manager filtering to branch B would be
   * listening in branch A and would see nothing arrive, which is the silent
   * subscription that hook's own doc comment exists to prevent.
   */
  const rooms = useMemo(() => (branchId ? [`branch:${branchId}`] : undefined), [branchId]);
  useRealtime(VENUE_EVENTS.PRINT_JOB_FAILED, reload, rooms);
  useRealtime(VENUE_EVENTS.PRINT_JOB_DEAD, reload, rooms);
  // Socket.IO replays nothing across a blip, so a screen that only patched from
  // events would carry on looking live while showing a state that stopped being
  // true at the moment of the drop.
  useRealtimeResync(reload);

  /**
   * Worst first, within the page the server sent.
   *
   * Deliberately a *presentation* sort and nothing more: the API orders by
   * `queuedAt` and has no notion of severity, so this cannot promote a dead job
   * from page two. The rollup tiles above are the honest way to be sure you are
   * seeing every failure — they count the whole branch, and clicking one filters
   * the query rather than re-sorting a page.
   */
  const ordered = useMemo(
    () =>
      [...jobs].sort((a, b) => {
        const bySeverity = JOB_VISUALS[b.status].severity - JOB_VISUALS[a.status].severity;
        if (bySeverity !== 0) return bySeverity;
        return new Date(b.queuedAt).getTime() - new Date(a.queuedAt).getTime();
      }),
    [jobs],
  );

  const act = async (job: PrintJobRow, run: () => Promise<unknown>, success: string) => {
    setBusyId(job.id);
    try {
      await run();
      toast.success(success);
      reload();
    } catch (error) {
      const code = errorCode(error);
      if (code === PRINT_ERROR_CODES.jobNotPrinted) {
        /**
         * One code, two refusals, and they want opposite things from the
         * operator. The paper-jam case carries the job's `status` and means
         * "retry instead" — a jammed job is going to print on its own and
         * reprinting it is the duplicate this epic exists to prevent. The
         * retention case carries no status: the job did print, but its document
         * was scrubbed after seven days, so there is nothing to copy and the
         * answer is to re-issue from the source. A single "failed" toast would
         * send the operator to press the same button again in the first case
         * and to hunt for a printer fault in the second.
         */
        const refusedStatus = (error as { response?: { data?: { status?: unknown } } })?.response
          ?.data?.status;
        toast.error(
          refusedStatus ? t('fleet.spool.reprintRefused') : t('fleet.spool.reprintScrubbed'),
        );
      } else if (code === PRINT_ERROR_CODES.jobNotRetryable) {
        toast.error(t('fleet.spool.notRetryable'));
      } else {
        toast.error(apiMessage(error, t('fleet.errors.actionFailed')));
      }
      reload();
    } finally {
      setBusyId(null);
    }
  };

  const retry = (job: PrintJobRow) =>
    act(job, () => api.post(FLEET_ROUTES.jobRetry(job.id)), t('fleet.spool.retried'));

  const reprint = async (job: PrintJobRow) => {
    const ok = await confirm.confirm({
      title: t('fleet.spool.reprintTitle'),
      body: t('fleet.spool.reprintConfirm', {
        document: t(`fleet.documentType.${job.documentType}`),
        destination: job.destination?.name ?? t('fleet.common.unknownDestination'),
      }),
      confirmLabel: t('fleet.spool.reprint'),
    });
    if (!ok) return;
    // Required by the server, and required here for the reason it is required
    // there: a double-tapped button carrying the same key collapses to one copy.
    const operationId = operationKey('reprint');
    await act(
      job,
      () =>
        api.post(FLEET_ROUTES.jobReprint(job.id), { operationId }, {
          headers: { 'X-Operation-Id': operationId },
        }),
      t('fleet.spool.reprinted'),
    );
  };

  const cancel = async (job: PrintJobRow) => {
    const reason = await confirm.prompt({
      title: t('fleet.spool.cancelTitle'),
      body: t('fleet.spool.cancelConfirm'),
      label: t('fleet.spool.cancelReason'),
      placeholder: t('fleet.spool.cancelReasonPlaceholder'),
      confirmLabel: t('fleet.spool.cancel'),
      tone: 'danger',
    });
    if (reason === null) return;
    await act(
      job,
      () => api.post(FLEET_ROUTES.jobCancel(job.id), reason.trim() ? { reason: reason.trim() } : {}),
      t('fleet.spool.cancelled'),
    );
  };

  const rollup = rollupQuery.data;

  return (
    <div className="space-y-4">
      {/* The rollup: counts, because the question is "is anything broken", asked
          between covers, and forty healthy rows bury the one that is not. */}
      {canSeeRollup && rollup && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <RollupTile
            label={t('fleet.spool.tiles.queued')}
            value={rollup.jobs.queued}
            active={status === 'QUEUED'}
            onClick={() => setStatus(status === 'QUEUED' ? '' : 'QUEUED')}
            tone="info"
          />
          <RollupTile
            label={t('fleet.spool.tiles.failed')}
            value={rollup.jobs.failed}
            active={status === 'FAILED'}
            onClick={() => setStatus(status === 'FAILED' ? '' : 'FAILED')}
            tone={rollup.jobs.failed > 0 ? 'danger' : 'quiet'}
          />
          <RollupTile
            label={t('fleet.spool.tiles.dead')}
            hint={t('fleet.spool.tiles.deadHint')}
            value={rollup.jobs.dead}
            active={status === 'DEAD'}
            onClick={() => setStatus(status === 'DEAD' ? '' : 'DEAD')}
            tone={rollup.jobs.dead > 0 ? 'danger' : 'quiet'}
          />
          <RollupTile
            label={t('fleet.spool.tiles.hardware')}
            hint={t('fleet.spool.tiles.hardwareHint', {
              printers: rollup.printers.inTrouble,
              bridges: rollup.bridges.stale,
            })}
            value={rollup.printers.inTrouble + rollup.bridges.stale}
            tone={rollup.printers.inTrouble + rollup.bridges.stale > 0 ? 'warning' : 'quiet'}
          />
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        {branches.length > 0 && (
          <Select
            className="w-auto min-w-[10rem]"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            aria-label={t('fleet.common.branch')}
          >
            <option value="">{t('fleet.common.allBranches')}</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        )}
        <Select
          className="w-auto min-w-[10rem]"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label={t('fleet.common.status')}
        >
          <option value="">{t('fleet.spool.allStatuses')}</option>
          {STATUSES.map((option) => (
            <option key={option} value={option}>
              {t(`fleet.jobStatus.${option}`)}
            </option>
          ))}
        </Select>
        <Select
          className="w-auto min-w-[10rem]"
          value={documentType}
          onChange={(e) => setDocumentType(e.target.value)}
          aria-label={t('fleet.spool.document')}
        >
          <option value="">{t('fleet.spool.allDocuments')}</option>
          {DOCUMENT_TYPES.map((option) => (
            <option key={option} value={option}>
              {t(`fleet.documentType.${option}`)}
            </option>
          ))}
        </Select>
        {canSeePrinters && destinations.length > 0 && (
          <Select
            className="w-auto min-w-[10rem]"
            value={destinationId}
            onChange={(e) => setDestinationId(e.target.value)}
            aria-label={t('fleet.common.destination')}
          >
            <option value="">{t('fleet.spool.allDestinations')}</option>
            {destinations.map((destination) => (
              <option key={destination.id} value={destination.id}>
                {destination.name}
              </option>
            ))}
          </Select>
        )}
        {canSeePrinters && printers.length > 0 && (
          <Select
            className="w-auto min-w-[10rem]"
            value={printerId}
            onChange={(e) => setPrinterId(e.target.value)}
            aria-label={t('fleet.common.printer')}
          >
            <option value="">{t('fleet.spool.allPrinters')}</option>
            {printers.map((printer) => (
              <option key={printer.id} value={printer.id}>
                {printer.name}
              </option>
            ))}
          </Select>
        )}

        <div className="ms-auto flex items-center gap-2">
          {/* Freshness, stated. A screen that silently stopped refreshing looks
              exactly like a venue where nothing is happening. */}
          <span className="text-xs text-ink-muted" aria-live="polite">
            {jobsQuery.loading
              ? t('fleet.common.refreshing')
              : live
                ? t('fleet.common.liveEvery', { seconds: REFRESH_MS / 1000 })
                : t('fleet.common.paused')}
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setLive((value) => !value)}
            aria-pressed={live}
            title={live ? t('fleet.common.pause') : t('fleet.common.resume')}
          >
            <Icon as={live ? Pause : Play} />
            <span className="sr-only">{live ? t('fleet.common.pause') : t('fleet.common.resume')}</span>
          </Button>
          <Button size="sm" variant="outline" onClick={reload} disabled={jobsQuery.loading}>
            <Icon as={RefreshCw} />
            {t('fleet.common.refresh')}
          </Button>
        </div>
      </div>

      <PanelResult
        loading={jobsQuery.loading && !jobsQuery.data}
        error={jobsQuery.error}
        count={ordered.length}
        onRetry={reload}
        subject={t('fleet.spool.subject')}
        filtered={filtered}
        onClearFilters={clearFilters}
        emptyTitle={t('fleet.spool.emptyTitle')}
        emptyHint={t('fleet.spool.emptyHint')}
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('fleet.common.status')}</TH>
                <TH>{t('fleet.spool.document')}</TH>
                <TH>{t('fleet.spool.where')}</TH>
                <TH>{t('fleet.spool.attempts')}</TH>
                <TH>{t('fleet.spool.queuedAt')}</TH>
                <TH>{t('fleet.spool.whatWentWrong')}</TH>
                <TH className="text-end">{t('fleet.common.actions')}</TH>
              </TR>
            </THead>
            <tbody>
              {ordered.map((job) => {
                const visual = JOB_VISUALS[job.status];
                const busy = busyId === job.id;
                return (
                  <TR key={job.id} className={cn(rowStateClass(visual))}>
                    <TD>
                      <JobStatusChip status={job.status} />
                      {job.isReprint && (
                        <p className="mt-1 text-xs text-ink-muted">{t('fleet.spool.isReprint')}</p>
                      )}
                    </TD>
                    <TD>
                      <span className="font-medium">
                        {t(`fleet.documentType.${job.documentType}`)}
                      </span>
                      {job.sourceType && (
                        <p className="text-xs text-ink-muted">
                          {t('fleet.spool.source', { source: job.sourceType })}
                        </p>
                      )}
                    </TD>
                    <TD>
                      <span>{job.destination?.name ?? t('fleet.common.unknownDestination')}</span>
                      <p className="text-xs text-ink-muted">
                        {job.printer
                          ? job.printer.name
                          : /* Resolved at claim time, not at enqueue: a queued job
                               genuinely has no printer yet, which is not a fault. */
                            t('fleet.spool.printerAtClaim')}
                      </p>
                    </TD>
                    <TD className="tabular-nums">
                      {t('fleet.spool.attemptsOf', {
                        attempts: job.attempts,
                        max: job.maxAttempts,
                      })}
                    </TD>
                    <TD className="text-ink-muted">{formatDateTime(job.queuedAt, i18n.language)}</TD>
                    <TD className="max-w-[22rem]">
                      {job.lastError ? (
                        <span className="line-clamp-2 text-red-700 dark:text-red-400" title={job.lastError}>
                          {job.lastError}
                        </span>
                      ) : (
                        <span className="text-ink-muted">{t('fleet.common.dash')}</span>
                      )}
                    </TD>
                    <TD>
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setOpenJobId(job.id)}
                          title={t('fleet.spool.diagnose')}
                        >
                          <Icon as={Search} />
                          <span className="sr-only">{t('fleet.spool.diagnose')}</span>
                        </Button>
                        {canRetry && RETRYABLE.includes(job.status) && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busy}
                            onClick={() => void retry(job)}
                          >
                            <Icon as={RotateCcw} />
                            {t('fleet.spool.retry')}
                          </Button>
                        )}
                        {/* Only ever offered on a job that printed. The server
                            refuses the rest, and offering a button whose only
                            outcome is a refusal teaches operators to ignore
                            refusals. */}
                        {canReprint && job.status === 'PRINTED' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busy}
                            onClick={() => void reprint(job)}
                            title={t('fleet.spool.reprint')}
                          >
                            <Icon as={Copy} />
                            <span className="sr-only">{t('fleet.spool.reprint')}</span>
                          </Button>
                        )}
                        {canCancel && CANCELLABLE.includes(job.status) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busy}
                            onClick={() => void cancel(job)}
                            title={t('fleet.spool.cancel')}
                          >
                            <Icon as={Ban} className="text-red-600 dark:text-red-400" />
                            <span className="sr-only">{t('fleet.spool.cancel')}</span>
                          </Button>
                        )}
                      </div>
                    </TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        </TableWrap>
      </PanelResult>

      <PrintJobDetailDialog
        jobId={openJobId}
        onClose={() => setOpenJobId(null)}
        onChanged={reload}
      />
    </div>
  );
}

/**
 * One count from the rollup, and — where the count maps onto a filter — the
 * button that shows the rows behind it.
 *
 * A zero is drawn quiet rather than green: "no failures" is the normal state of
 * this screen, and a wall of green tiles is a wall nobody reads, which is the
 * same reason `bypassTenantIsolation()` was rejected in the ADR.
 */
function RollupTile({
  label,
  hint,
  value,
  tone,
  active,
  onClick,
}: {
  label: string;
  hint?: string;
  value: number;
  tone: 'info' | 'warning' | 'danger' | 'quiet';
  active?: boolean;
  onClick?: () => void;
}) {
  const body = (
    <>
      <p className="text-sm text-ink-muted">{label}</p>
      <p
        className={cn(
          'text-2xl font-semibold tabular-nums',
          tone === 'danger' && 'text-red-600 dark:text-red-400',
          tone === 'warning' && 'text-amber-600 dark:text-amber-400',
        )}
      >
        {value}
      </p>
      {hint && <p className="text-xs text-ink-muted">{hint}</p>}
    </>
  );

  const shell = cn(
    'rounded-xl border bg-surface p-4 text-start shadow-sm',
    tone === 'danger' && 'border-red-300 dark:border-red-900',
    tone === 'warning' && 'border-amber-300 dark:border-amber-900',
    active && 'ring-2 ring-ring',
  );

  if (!onClick) return <div className={shell}>{body}</div>;
  return (
    <button type="button" onClick={onClick} aria-pressed={active} className={cn(shell, 'hover:bg-hover')}>
      {body}
    </button>
  );
}
